/* eslint-disable simple-import-sort/imports */
import '@formatjs/intl-datetimeformat/add-golden-tz' // Replace with add-all-tz if the need arises
import '@formatjs/intl-datetimeformat/locale-data/hu'
import '@formatjs/intl-datetimeformat/polyfill'
import '@formatjs/intl-locale/polyfill'

import {
    addDays,
    eachDayOfInterval,
    endOfDay,
    endOfMonth,
    endOfWeekWithOptions,
    differenceInDays as fnsDifferenceInDays,
    differenceInWeeksWithOptions as fnsDifferenceInWeeks,
    format,
    getDay,
    getISOWeek,
    isAfter,
    isBefore,
    isEqual,
    isSameDay,
    isSameWeekWithOptions,
    setDate,
    startOfDay,
    startOfMonth,
    startOfWeekWithOptions,
    subDays,
} from 'date-fns/fp'

import {
    createDate,
    DateInput,
    DatePeriodVariant,
    DateRangeFormat,
    DateRangeInput,
    getCurrentDateLocaleCode,
    rangeToDate,
} from '@longy/common'
import { TFunction } from 'i18next'
import { getTimeZone } from './getTimeZone'

const toLocaleString = (date: DateInput, options?: Intl.DateTimeFormatOptions) =>
    createDate(date).toLocaleString(getCurrentDateLocaleCode(), {
        timeZone: getTimeZone(),
        ...options,
    })

export const formatChartDate = (date: DateInput) =>
    toLocaleString(date, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    })

export const formatChartTime = (date: DateInput) =>
    toLocaleString(date, {
        hour: '2-digit',
        minute: '2-digit',
    })

export const formatChartDateTime = (date: DateInput) =>
    toLocaleString(date, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    })

export const formatChartDateTimeLongMonth = (date: DateInput) =>
    toLocaleString(date, {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    })

const isMidnight = (date: DateInput) =>
    createDate(date).toLocaleTimeString(getCurrentDateLocaleCode(), {
        timeZone: getTimeZone(),
    }) === '00:00:00'

export const formatMeasuredAt = (date: DateInput) =>
    isMidnight(date) ? formatChartDate(date) : formatChartDateTime(date)

export const formatMeasuredAtShort = (date: DateInput) => formatChartDate(date)

export const formatYear = (date: DateInput) =>
    toLocaleString(date, {
        year: 'numeric',
    })

export const formatYearMonth = (date: DateInput) =>
    toLocaleString(date, {
        year: 'numeric',
        month: 'long',
    })

export const formatMonth = (date: DateInput, format: 'short' | 'long' = 'short') =>
    toLocaleString(date, {
        month: format,
    })

export const formatDayOfMonth = (date: DateInput) =>
    toLocaleString(date, {
        day: 'numeric',
    })

export const formatMonthDay = (date: DateInput) =>
    toLocaleString(date, {
        month: 'long',
        day: 'numeric',
    })

export const formatWeekOfYear = (date: DateInput) => `${getISOWeek(createDate(date))}. hét`

export const formatVerboseMonthDay = (date: DateInput) =>
    toLocaleString(date, {
        month: 'long',
        day: '2-digit',
    })

export const formatDateRange = (
    range: DateRangeInput,
    format: DateRangeFormat | undefined = 'short',
    showYear = false,
) => {
    const options =
        format === 'time'
            ? ({ hour: 'numeric', minute: '2-digit' } as const)
            : ({ month: format, day: 'numeric' } as const)
    const formatter = new Intl.DateTimeFormat(getCurrentDateLocaleCode(), {
        timeZone: getTimeZone(),
        ...options,
        year: showYear ? 'numeric' : undefined,
    })

    return formatter.formatRange(createDate(range.from), createDate(range.to))
}

export const formatDateRangeWithYear = (
    range: DateRangeInput,
    format: DateRangeFormat | undefined = 'short',
) => {
    const options =
        format === 'time'
            ? ({ hour: 'numeric', minute: '2-digit' } as const)
            : ({ month: format, day: 'numeric' } as const)

    const formatter = new Intl.DateTimeFormat(getCurrentDateLocaleCode(), {
        timeZone: getTimeZone(),
        ...options,
        year: 'numeric',
    })

    return formatter.formatRange(createDate(range.from), createDate(range.to))
}

export const startOfWeek = startOfWeekWithOptions({ weekStartsOn: 1 })
export const endOfWeek = endOfWeekWithOptions({ weekStartsOn: 1 })
const isSameWeek = isSameWeekWithOptions({ weekStartsOn: 1 })

export const resolvePeriod = (dateRange: DateRangeInput): DatePeriodVariant => {
    const { from, to } = rangeToDate(dateRange)

    switch (true) {
        case isSameDay(from, to):
            return 'day'
        case isSameWeek(from, to):
            return 'week'
        default:
            return 'month'
    }
}

export const dateToRange = (date: DateInput, period: DatePeriodVariant) => {
    const from = createDate(date)

    switch (period) {
        case 'day':
            return { from, to: endOfDay(from) }
        case 'week':
            return { from, to: endOfWeek(from) }
        case 'month':
            return { from, to: endOfMonth(from) }
    }
}

const isValidDateInput = (v: unknown): v is DateInput =>
    v instanceof Date ||
    ((typeof v === 'number' || typeof v === 'string') && !isNaN(new Date(v).getTime()))

export const isValidDateRange = (v: unknown): v is DateRangeInput =>
    !!v &&
    typeof v === 'object' &&
    'from' in v &&
    isValidDateInput(v.from) &&
    'to' in v &&
    isValidDateInput(v.to)

export const formatIsoWithTz = (date: Date) => format("yyyy-MM-dd'T'HH:mm:ss.SSSxx", date)

export const createDateRangeArray = (dateRange: DateRangeInput) =>
    eachDayOfInterval({
        start: createDate(dateRange.from),
        end: createDate(dateRange.to),
    })

export const differenceInWeeks = (startDate: Date, endDate: Date) => {
    let diff = (endDate.getTime() - startDate.getTime()) / 1000
    diff /= 60 * 60 * 24 * 7

    return Math.abs(Math.round(diff))
}

export const differenceInDays = (startDate: Date, endDate: Date) => {
    const diff = fnsDifferenceInDays(endDate, startDate)

    return Math.abs(diff)
}

export const currentPregnancyWeek = (startDate: Date, endDate: Date) => {
    const weeks = fnsDifferenceInWeeks(
        {
            roundingMethod: 'ceil',
        },
        startDate,
        endDate,
    )

    return Math.abs(weeks)
}

export const displayPeriodOfDay = (date: DateInput, t: TFunction) => {
    const dateObject = new Date(date)
    const hour = dateObject.getHours()

    if (hour >= 5 && hour < 12) {
        return t('common.timeOfDays.morning')
    } else if (hour >= 12 && hour < 17) {
        return t('common.timeOfDays.noon')
    } else if (hour >= 17 && hour < 21) {
        return t('common.timeOfDays.evening')
    }

    return t('common.timeOfDays.night')
}

export {
    addDays,
    endOfDay,
    endOfMonth,
    getDay,
    isAfter,
    isBefore,
    isEqual,
    setDate,
    startOfDay,
    startOfMonth,
    subDays,
}
